/**
 * SRV-FG-5-3: External Service Forwarding Section
 * Shows forwarding status banner and controls on WorksheetDetailPage
 *
 * Q-T309: a ket modalis a repo `Dialog`-jat (Radix) hasznalja, nem nyers
 * `<div className="fixed inset-0">` + `Card` osszeallitast. A valtas HAROM
 * kiutat ad egyszerre, es a QA-105 fixet is magaban foglalja:
 *   - `Esc`                -> a Radix `DismissableLayer`-e zar
 *   - hatter-kattintas     -> ugyanaz, `onPointerDownOutside`-on
 *   - gorgetes / magassag  -> a `DialogContent` sajat `maxHeight: 85vh`
 *                             + `overflowY: auto` stilusa (a QA-105-ben kezzel
 *                             tett `max-h-[80vh] overflow-y-auto` helyett)
 * Plusz, amit a nyers div nem adott: focus-trap, `role="dialog"`, `aria-modal`.
 *
 * A QA-105 eredeti indoka valtozatlanul all: a `variant="list"` partner-valaszto
 * szuretlen kereseskor 18+ sort rajzol (`pageSize: 50`), es magassag-korlat
 * nelkul a kartya 2025 px lett — a gombok a 650-es viewporton KIVUL rekedtek.
 */

import type { Partner } from '@/api/partners';
import {
  type ExternalForwardRecord,
  forwardToExternal,
  getExternalForward,
  returnFromExternal,
} from '@/api/worksheets';
import { PartnerSearchSelect } from '@/components/partners';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';

interface ExternalForwardSectionProps {
  worksheetId: string;
  isEditable: boolean;
  onRefresh: () => void;
}

export function ExternalForwardSection({
  worksheetId,
  isEditable,
  onRefresh,
}: ExternalForwardSectionProps) {
  const { t } = useTranslation();
  const [record, setRecord] = useState<ExternalForwardRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);

  // Forward form state
  // QA-103: a nyers UUID-mező helyett kiválasztott partner. A payload továbbra is
  // az `id`-t viszi (a BE szerződése változatlan), a pultos viszont NEVET lát.
  const [partner, setPartner] = useState<Partner | null>(null);
  const [reason, setReason] = useState('');
  const [estimatedReturn, setEstimatedReturn] = useState('');
  const [forwarding, setForwarding] = useState(false);

  // Return form state
  const [actualCost, setActualCost] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const [returning, setReturning] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const fetchForwardStatus = useCallback(async () => {
    try {
      const response = await getExternalForward(worksheetId);
      setRecord(response.data);
    } catch {
      // Not critical
    } finally {
      setLoading(false);
    }
  }, [worksheetId]);

  useEffect(() => {
    void fetchForwardStatus();
  }, [fetchForwardStatus]);

  const handleForward = async () => {
    setError(null);
    if (!partner || !reason.trim() || !estimatedReturn) {
      setError(t('service.external.error'));
      return;
    }

    setForwarding(true);
    try {
      const response = await forwardToExternal(worksheetId, {
        externalPartnerId: partner.id,
        reason: reason.trim(),
        estimatedReturnDate: estimatedReturn,
      });
      setRecord(response.data);
      setShowForwardModal(false);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('service.external.error'));
    } finally {
      setForwarding(false);
    }
  };

  const handleReturn = async () => {
    setError(null);
    const cost = Number(actualCost);
    if (isNaN(cost) || cost < 0) {
      setError(t('service.external.error'));
      return;
    }

    setReturning(true);
    try {
      const response = await returnFromExternal(worksheetId, {
        actualCost: cost,
        notes: returnNotes.trim(),
      });
      setRecord(response.data);
      setShowReturnModal(false);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('service.external.error'));
    } finally {
      setReturning(false);
    }
  };

  if (loading) return null;

  // Show banner if forwarded
  if (record?.status === 'FORWARDED') {
    return (
      <div className="mb-6 rounded-lg border border-indigo-400 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-950/20 p-4">
        <div className="flex items-start gap-3">
          <svg
            className="h-5 w-5 mt-0.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
          <div className="flex-1">
            <h3 className="font-semibold text-indigo-800 dark:text-indigo-300">
              {t('service.external.banner')}
            </h3>
            <p className="text-sm text-indigo-700 dark:text-indigo-400 mt-1">
              {t('service.external.bannerInfo', {
                partner: record.externalPartnerName ?? record.externalPartnerId,
                date: new Date(record.estimatedReturnDate).toLocaleDateString('hu-HU'),
              })}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{record.reason}</p>
          </div>
          {isEditable && (
            <Button
              size="sm"
              variant="outline"
              className="border-indigo-400 text-indigo-600"
              onClick={() => setShowReturnModal(true)}
            >
              {t('service.external.returnButton')}
            </Button>
          )}
        </div>

        {/* Return modal */}
        <Dialog
          open={showReturnModal}
          onOpenChange={isOpen => {
            if (!isOpen) setShowReturnModal(false);
          }}
        >
          <DialogContent
            className="w-full max-w-md"
            data-testid="external-return-modal"
            aria-describedby={undefined}
          >
            <DialogHeader>
              <DialogTitle>{t('service.external.returnModalTitle')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">
                  {t('service.external.actualCost')}
                </label>
                <Input
                  type="number"
                  min={0}
                  value={actualCost}
                  onChange={e => setActualCost(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">
                  {t('service.external.returnNotes')}
                </label>
                <textarea
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-background px-3 py-2 text-sm"
                  rows={3}
                  value={returnNotes}
                  onChange={e => setReturnNotes(e.target.value)}
                  placeholder={t('service.external.returnNotesPlaceholder')}
                />
              </div>
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowReturnModal(false)}
                  disabled={returning}
                >
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleReturn} disabled={returning}>
                  {returning ? t('common.saving') : t('service.external.returnButton')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Show returned info
  if (record?.status === 'RETURNED') {
    return (
      <div className="mb-6 rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950/20 p-3">
        <p className="text-sm text-green-800 dark:text-green-300">
          {t('service.external.returnSuccess')} —{' '}
          {record.externalPartnerName ?? record.externalPartnerId}
          {record.actualCost !== undefined && record.actualCost > 0
            ? ` (${new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 }).format(record.actualCost)})`
            : ''}
        </p>
      </div>
    );
  }

  // Show forward button if editable and not yet forwarded
  if (!isEditable) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="border-indigo-400 text-indigo-600 dark:text-indigo-400"
        onClick={() => setShowForwardModal(true)}
      >
        {t('service.external.forwardButton')}
      </Button>

      {/* Forward modal */}
      <Dialog
        open={showForwardModal}
        onOpenChange={isOpen => {
          if (!isOpen) setShowForwardModal(false);
        }}
      >
        <DialogContent className="w-full max-w-md" data-testid="external-forward-modal">
          <DialogHeader>
            <DialogTitle>{t('service.external.forwardModalTitle')}</DialogTitle>
            <DialogDescription>{t('service.external.forwardModalDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                {t('service.external.partnerSelect')}
              </label>
              <PartnerSearchSelect
                variant="list"
                allowCreate={false}
                value={partner}
                onSelect={setPartner}
                placeholder={t('service.external.partnerSearchPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                {t('service.external.reason')}
              </label>
              <textarea
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-background px-3 py-2 text-sm"
                rows={3}
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder={t('service.external.reasonPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">
                {t('service.external.estimatedReturn')}
              </label>
              <Input
                type="date"
                value={estimatedReturn}
                onChange={e => setEstimatedReturn(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowForwardModal(false)}
                disabled={forwarding}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleForward} disabled={forwarding}>
                {forwarding ? t('service.external.forwarding') : t('service.external.forward')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
